import { useLocation } from 'react-router-dom';
import { NavigationItem } from './useNavigation';
import { useAccess } from '../components/access';
import { useDemo } from '../components/demo/DemoProvider';

export function useNavigationState() {
  const location = useLocation();
  const { access } = useAccess();
  const { isDemo } = useDemo();

  const isItemActive = (item: NavigationItem): boolean => {
    return (
      location.pathname === item.href ||
      (item.href !== '/' && location.pathname.startsWith(item.href + '/'))
    );
  };

  const isItemAccessible = (item: NavigationItem): boolean => {
    // In unauthenticated preview, items are not accessible (clicks will redirect to login)
    if (!access) return false;
    const inDemo = isDemo && item.demo;
    // Special case: Files tab (/browse) is not accessible
    if (item.href === '/browse') {
      return false;
    }

    // When in demo mode, only items with demo: true are accessible
    if (isDemo && !item.demo) {
      return false;
    }

    const permissionOk = !item.permission || access.permissions.has(item.permission);
    const featureOk = !item.feature || access.features.has(item.feature);
    return inDemo || (permissionOk && featureOk);
  };

  const isItemVisible = (item: NavigationItem): boolean => {
    // In unauthenticated preview, show all items (feature visibility checks bypassed)
    if (!access) return false;
    const inDemo = isDemo && item.demo;

    if (item.href === '/browse') {
      return false;
    }

    // When in demo mode, only show items that have demo: true
    if (isDemo && !item.demo) {
      return false;
    }

    if (item.limit_visibility) {
      // Only show if user has BOTH the required feature and permission (if set)
      const permissionOk = !item.permission || access.permissions.has(item.permission);
      const featureOk = !item.feature || access.features.has(item.feature);
      return inDemo || (permissionOk && featureOk);
    }
    // Show item if feature requirement is met (regardless of permission for pro upgrade)
    return inDemo || !item.feature || access.features.has(item.feature);
  };

  return {
    isItemActive,
    isItemAccessible,
    isItemVisible,
  };
}
